import type { MetaTiendaVista, MotivoSinMeta, ReglaMeta, TotalesCaptura } from '../types/metas.types';
import { esMetaABodeguero } from './bodeguero';
import { esFilaCasa } from './casa';

/**
 * Totales EN VIVO mientras el supervisor escribe — la misma regla que
 * `totalesCaptura` en `domain/validar-captura.ts` de la API:
 *
 * - con meta: tiene monto (aunque el monto esté mal; eso lo dice la celda);
 * - sin meta: no tiene monto y sí motivo;
 * - sin meta por puesto: ni una cosa ni otra, y su puesto NO la obliga
 *   (`reglaMeta` `opcional` o `no_lleva`: gerentes, cajeros, bodegueros);
 * - pendiente: ni una cosa ni otra y su puesto la obliga (sin regla = `obligatoria`).
 *
 * Además, por la REGLA del puesto (21-oct-2026, «Colaboradores listos» igual
 * en la app y en el admin): cuántas filas la obligan (`obligatorias`), cuántas
 * la llevan opcional (gerente, subgerente) y cuántas no la llevan, tengan meta
 * o no. «Listos» = de las obligatorias, cuántas tienen meta o motivo.
 *
 * LA CASA (23-oct-2026, ronda F): no es una persona. No entra en `total` ni en
 * ningún conteo (con meta, sin meta, por puesto, pendientes, obligatorias…);
 * su meta SÍ entra en `suma` y en la diferencia contra la tienda («su meta
 * cuenta para cuadrar la meta de la tienda»), y se dice aparte en `metaCasa`.
 *
 * Si divergen, el panel diría una suma y el regional vería otra. La prueba de
 * al lado fija los mismos casos.
 */
export interface FilaTotalizable {
  metaMonto: number | null | undefined;
  sinMetaMotivo: MotivoSinMeta | string | null | undefined;
  reglaMeta?: ReglaMeta | null;
  /** Puesto de bodega: con meta (23-oct-2026) ya no es «sin meta por su puesto». */
  esBodega?: boolean;
  /** La Casa (ronda F): por la llave `casa:` o la marca de la API. */
  key?: string;
  esCasa?: boolean;
}

/** Sólo una fila cuyo puesto OBLIGA a llevar meta queda pendiente sin meta ni motivo. La Casa, nunca. */
export const esPendiente = (f: FilaTotalizable): boolean =>
  (f.metaMonto === null || f.metaMonto === undefined) &&
  !f.sinMetaMotivo &&
  (f.reglaMeta ?? 'obligatoria') === 'obligatoria' &&
  !esFilaCasa(f);

export function totalesCaptura(
  filas: FilaTotalizable[],
  metaTienda: number | null,
): TotalesCaptura {
  let conMeta = 0;
  let sinMeta = 0;
  let sinMetaPorPuesto = 0;
  let pendientes = 0;
  let obligatorias = 0;
  let opcionales = 0;
  let noLleva = 0;
  let suma = 0;
  let casas = 0;
  let metaCasa = 0;
  for (const f of filas) {
    // La Casa no es persona: sólo su meta, en la suma (ronda F).
    if (esFilaCasa(f)) {
      casas++;
      if (f.metaMonto !== null && f.metaMonto !== undefined) {
        const m = Number(f.metaMonto) || 0;
        suma += m;
        metaCasa += m;
      }
      continue;
    }
    const regla = f.reglaMeta ?? 'obligatoria';
    if (regla === 'opcional') opcionales++;
    // Un bodeguero CON meta (23-oct-2026) no es «sin meta por su puesto»: no entra en ningún grupo.
    else if (regla === 'no_lleva') {
      if (!esMetaABodeguero(f)) noLleva++;
    } else obligatorias++;
    if (f.metaMonto !== null && f.metaMonto !== undefined) {
      conMeta++;
      suma += Number(f.metaMonto) || 0;
    } else if (f.sinMetaMotivo) sinMeta++;
    else if (esPendiente(f)) pendientes++;
    else sinMetaPorPuesto++;
  }
  suma = Math.round(suma * 100) / 100;
  metaCasa = Math.round(metaCasa * 100) / 100;
  const diferencia = metaTienda !== null ? Math.round((suma - metaTienda) * 100) / 100 : null;
  const diferenciaPct =
    metaTienda !== null && metaTienda > 0 ? (suma - metaTienda) / metaTienda : null;
  return {
    total: filas.length - casas,
    conMeta,
    sinMeta,
    sinMetaPorPuesto,
    pendientes,
    obligatorias,
    opcionales,
    noLleva,
    suma,
    ...(metaCasa > 0 ? { metaCasa } : {}),
    metaTienda,
    diferencia,
    diferenciaPct,
  };
}

/**
 * La meta de tienda contra la que se compara. Sólo cuenta si están los 7 días
 * SIN IVA: una suma de 5 días no es la meta de la semana, y una meta en $0 es
 * «sin meta» (misma decisión que la API).
 */
export const metaTiendaComparable = (m: MetaTiendaVista | null | undefined): number | null =>
  m && m.completa && !m.conIva && m.monto !== null && m.monto > 0 ? m.monto : null;
